'use client';

import {
  AlertCircleIcon,
  ArrowDown01Icon,
  Cancel01Icon,
} from '@terra/ui-icons';
import { useState } from 'react';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Button } from '../../../components/button';
import { IconButton } from '../../../components/icon-button';
import { Text } from '../../../components/styled/text';
import { useTranslations } from 'next-intl';

type ChatErrorMessageProps = {
  errorMessage: string | null;
  onRetry?: () => void;
  onClose?: () => void;
};

export const ChatErrorMessage = ({
  errorMessage,
  onRetry,
  onClose,
}: ChatErrorMessageProps) => {
  const t = useTranslations('designSystem.chat.chat');
  const [open, setOpen] = useState(false);
  const errorString = typeof errorMessage === 'string' ? errorMessage : null;

  return (
    <Box w="full" minW={0}>
      <VStack
        gap={1.5}
        alignItems="flex-start"
        px={2}
        py={1.5}
        w="full"
        minW={0}
      >
        <HStack
          w="full"
          gap={2}
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          minW={0}
        >
          <HStack
            gap={1.5}
            alignItems="center"
            minW={0}
            flex={1}
            cursor="pointer"
            _hover={{ opacity: 0.9 }}
            onClick={() => setOpen(!open)}
          >
            <Box
              color="fg.error"
              display="flex"
              alignItems="center"
              flexShrink={0}
            >
              <AlertCircleIcon size={14} />
            </Box>
            <Text fontSize="xs" color="fg.default" fontWeight="medium" truncate>
              {t('errorOccurred')}
            </Text>
            <Box
              color="fg.muted"
              display="flex"
              alignItems="center"
              flexShrink={0}
              transform={open ? 'rotate(180deg)' : 'rotate(0deg)'}
              transition="transform 0.2s"
            >
              <ArrowDown01Icon size={14} />
            </Box>
          </HStack>
          <HStack gap={1} alignItems="center" flexShrink={0}>
            {onRetry && (
              <Button
                size="xs"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  onRetry();
                }}
              >
                {t('retry')}
              </Button>
            )}
            {onClose && (
              <IconButton
                size="xs"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
                aria-label="Close error"
              >
                <Cancel01Icon size={14} />
              </IconButton>
            )}
          </HStack>
        </HStack>
        {open && (
          <Box w="full" minW={0}>
            <Text
              fontSize="xs"
              color="fg.muted"
              wordBreak="break-word"
              lineHeight="relaxed"
              overflowWrap="break-word"
            >
              {errorString}
            </Text>
          </Box>
        )}
      </VStack>
    </Box>
  );
};
